import { RoleType, GenderType } from '@ace-clublabs/common-membership-api-types';
import { Box, Divider, Icon, Paper, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { GENDER_CODE_MAP, kebabCaseStr, ROLE_TYPE_MAP } from '../../../utils/stringHelper';
import { useHistory } from 'react-router-dom';
import { Pages } from '../../../utils/constants';
import { MSSSpinOffStatus } from '@lib/types';
import { SpinOffMessage } from '../../SpinOffMessage/SpinOffMessage';
import { changeAddressClick } from '../../../state/helpers';

export interface IMembersDetailCardProps {
  clubCode: string;
  customerId: string;
  name: string;
  roleType: RoleType;
  dateOfBirth?: string;
  gender?: GenderType;
  email?: string;
  mobile?: string;
  home?: string;
  residenceAddress?: string;
  mailingAddress?: string;
  isLoggedInMember: boolean;
  className?: string;
  spinOffStatus?: MSSSpinOffStatus;
}

export interface IAddressCellProps {
  title: string;
  address: string | undefined;
}

export const MembersDetailCard = ({
  clubCode,
  customerId,
  name,
  roleType,
  dateOfBirth,
  gender,
  email,
  mobile,
  home,
  residenceAddress,
  mailingAddress,
  isLoggedInMember,
  className,
  spinOffStatus,
}: IMembersDetailCardProps) => {
  const history = useHistory();

  const navigateToContactInfoPage = (customerId: string) => {
    if (isLoggedInMember) {
      //loggedInMember ContactInfoPage
      history.push(Pages.ContactInfoPage);
    } else {
      //familyMember ContactInfoPage
      history.push(`${Pages.ContactInfoPage}?custId=${customerId}`);
    }
  };

  const AddressCell = ({ title, address }: IAddressCellProps) => {
    if (!address) {
      return <></>;
    }
    const classNamePostfix = kebabCaseStr(title)?.split('-')?.[0] || '';

    return (
      <Stack>
        <Typography variant="b2Regular" sx={{ color: '#707070' }} className={`address-title-${classNamePostfix}`}>
          {title}
        </Typography>
        <Typography variant="b1Regular" className={`address-details-${classNamePostfix}`} data-qm-block="true">
          {address}
        </Typography>
      </Stack>
    );
  };

  const MembershipAddressInfo = () => {
    const sameAddresses = residenceAddress === mailingAddress;
    const showMailingAddress = !sameAddresses && !!mailingAddress;
    const residenceAddressTitle = showMailingAddress ? 'Residence address:' : 'Residence/mailing address:';
    return (
      <>
        <Stack marginLeft={'2.5rem'} gap={'1rem'}>
          <AddressCell title={residenceAddressTitle} address={residenceAddress} />
          {showMailingAddress && <AddressCell title={'Mailing address:'} address={mailingAddress} />}
        </Stack>
        <div
          onClick={() => {
            changeAddressClick();
          }}
          className="edit-address-icon"
        >
          <Icon
            iconName="pen-regular"
            sx={{
              fontSize: '1.5rem',
              color: '#1C4DA6',
              padding: '0.5rem',
              boxSizing: 'content-box',
              ':hover': { cursor: 'pointer', backgroundColor: '#EFF7FE', borderRadius: '1.25rem' },
            }}
          />
        </div>
      </>
    );
  };

  // const SpinOffMessage = (props: { spinOffStatus: MSSSpinOffStatus | undefined; id: string }) => {
  //   if (!props.spinOffStatus || props.spinOffStatus === MSSSpinOffStatus.NONE) return <></>;
  //   console.log('****', props.id);

  //   return (
  //     <Typography variant="b2Regular" sx={{ color: '#A24531', marginTop: '1rem' }} id={`member-spin-off-message`}>
  //       {props.spinOffStatus === MSSSpinOffStatus.SPIN_OFF ? (
  //         <>
  //           This member <strong>won't be renewed</strong> because they've reached the maximum age for a dependent.
  //         </>
  //       ) : (
  //         <>
  //           This member will be <strong>updated to an adult associate</strong> at renewal because they've reached the
  //           maximum age for a dependent.
  //         </>
  //       )}
  //     </Typography>
  //   );
  // };

  const showSpinOffStatusComponent =
    spinOffStatus && spinOffStatus in MSSSpinOffStatus && spinOffStatus !== MSSSpinOffStatus.NONE;

  //hiding address for:
  //1. not logged in
  //2. dependents
  //3. NNE associate or Dependent member
  const isNNEAssociateOrDE = clubCode === '036' && (roleType === RoleType.ASSOCIATE || roleType === RoleType.DEPENDENT);
  const hideAddressInfo = !isLoggedInMember || roleType === RoleType.DEPENDENT || isNNEAssociateOrDE;

  return (
    <Paper variant="elevation" elevation={8} sx={{ padding: '1.5rem 1rem', height: 'fit-content' }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        gap="0.5rem"
        justifyContent="space-between"
        className={`members-detail-card-container ${className}`}
      >
        <Stack direction="row" gap="0.5rem">
          <Icon
            iconName="user-group-duotone"
            color="secondary"
            sx={{
              fontSize: '2rem',
            }}
          />
          <Stack direction="column">
            <Stack direction="column">
              <Typography variant="h5" className="member-name" data-qm-block="true">
                {name}
              </Typography>
              <Typography variant="b2Regular" sx={{ color: '#707070' }} className="member-role">
                {ROLE_TYPE_MAP[roleType]}
              </Typography>
              {dateOfBirth && (
                <Typography variant="b1Regular" sx={{ marginTop: '1rem' }} className="member-dob" data-qm-block="true">
                  Date of birth: <strong>{dateOfBirth}</strong>
                </Typography>
              )}
              {gender && (
                <Typography variant="b1Regular" className="member-gender">
                  Gender: <strong>{GENDER_CODE_MAP[gender]}</strong>
                </Typography>
              )}
              {isLoggedInMember && (
                <>
                  {email && (
                    <Typography variant="b1Regular" className="member-email" data-qm-encrypt="true">
                      Email: <strong>{email}</strong>
                    </Typography>
                  )}
                  {mobile && (
                    <Typography variant="b1Regular" className="member-mobile-number" data-qm-block="true">
                      Mobile: <strong>{mobile}</strong>
                    </Typography>
                  )}
                  {home && (
                    <Typography variant="b1Regular" className="member-home-number" data-qm-block="true">
                      Home: <strong>{home}</strong>
                    </Typography>
                  )}
                </>
              )}

              {showSpinOffStatusComponent && (
                <Typography
                  variant="b2Regular"
                  sx={{ color: '#A24531', marginTop: '1rem' }}
                  id={`member-spin-off-message`}
                >
                  <SpinOffMessage spinOffStatus={spinOffStatus} />
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
        <div
          onClick={() => {
            navigateToContactInfoPage(customerId);
          }}
          className="edit-contact-icon"
        >
          <Icon
            iconName="pen-regular"
            sx={{
              fontSize: '1.5rem',
              color: '#1C4DA6',
              padding: '0.5rem',
              boxSizing: 'content-box',
              ':hover': { cursor: 'pointer', backgroundColor: '#EFF7FE', borderRadius: '1.25rem' },
            }}
          />
        </div>
      </Stack>
      {!hideAddressInfo && (
        <Box className="address-info-section">
          <Box sx={{ marginLeft: '2.5rem' }}>
            <Divider variant="fullWidth" sx={{ margin: '1rem 0', borderWidth: '1px' }} />
          </Box>
          <Stack direction="row" gap="0.5rem" justifyContent="space-between">
            <MembershipAddressInfo />
          </Stack>
        </Box>
      )}
    </Paper>
  );
};
